/**
 * AI call campaigns (`/ai-calls/campaigns`): whom the AI may ring, the preview
 * of who that is, and the queue (ring R3), which the automation works (ring R4).
 *
 * A HAND-WRITTEN MIRROR of `apps/api/src/modules/crm-calls/lib/outbound/campaign/`
 * (`types.ts`, `screen.ts`, `preview.ts`, `enrol.ts`, `store.ts`);
 * `aiCampaigns.test.ts` reads those files and holds the lists and bounds below
 * against them, so a refusal added on the server fails a test here instead of
 * rendering as a raw key.
 *
 * React-free: the presets, the draft and the patch are the parts worth testing.
 * What the angle changes in the draft lives in `aiCampaignAngle.ts`, re-exported
 * below. The automation's fields (on or off, tries, dates, assignee) have their
 * draft, body and patch pieces in `aiCampaignAutomation.ts`, composed into the
 * draft, body and patch here.
 */
import { api } from "./api";
import type { AudienceParams } from "./audienceFilter";
import type { OpeningReasonSlot } from "./aiOutbound";
import type { OfferFields } from "./aiCampaignAngle";
import {
  emptyAutomationDraft,
  toAutomationBody,
  toAutomationDraft,
  toAutomationPatch,
  type CampaignAutomationBody,
  type CampaignAutomationDraft,
  type CampaignAutomationView,
} from "./aiCampaignAutomation";

export { flowCardAngles, warnsNoneHeard, withAngle, withPreset, withoutClients, type OfferFields } from "./aiCampaignAngle";

/** The reason sentence per contact. Mirrors `OpeningReasons` (`settings-texts.ts`). */
export type OpeningReasons = Record<OpeningReasonSlot, string>;

const BASE = "/admin/crm/calls/ai-outbound/campaigns";

/** Mirrors `CAMPAIGN_STATUSES`. */
export const CAMPAIGN_STATUSES = ["draft", "active", "paused", "done"] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

/** Mirrors `CAMPAIGN_SOURCES`. */
export const CAMPAIGN_SOURCES = ["brain", "register"] as const;
export type CampaignSource = (typeof CAMPAIGN_SOURCES)[number];

/** Mirrors `TARGET_STATUSES`. */
export const TARGET_STATUSES = ["pending", "calling", "retry", "done", "skipped"] as const;
export type TargetStatus = (typeof TARGET_STATUSES)[number];

/** Mirrors `BRAIN_WORKED_VALUES`. */
export const BRAIN_WORKED_VALUES = ["untouched", "called", "planned"] as const;
export type BrainWorked = (typeof BRAIN_WORKED_VALUES)[number];

/** Mirrors `PHONE_KINDS`. */
export const PHONE_KINDS = ["any", "landline"] as const;
export type CampaignPhoneKind = (typeof PHONE_KINDS)[number];

/** Mirrors `OUTBOUND_FIT_DECISIONS`. */
export const FIT_DECISIONS = ["go", "hold", "no"] as const;
export type FitDecision = (typeof FIT_DECISIONS)[number];

/** Mirrors `OUTBOUND_KINDS` and `OUTBOUND_GOALS`. */
export const CAMPAIGN_KINDS = ["warm", "cold"] as const;
export type CampaignKind = (typeof CAMPAIGN_KINDS)[number];
export const CAMPAIGN_GOALS = ["meeting", "callback", "qualify", "send_report"] as const;
export type CampaignGoal = (typeof CAMPAIGN_GOALS)[number];

/**
 * Mirrors `CAMPAIGN_ANGLES` (ring R9): why the AI calls. An offer sells; an
 * alert rings only about a defect a recheck saw right before dialling, and its
 * opening's reason sentence is written by code.
 */
export const CAMPAIGN_ANGLES = ["offer", "alert"] as const;
export type CampaignAngle = (typeof CAMPAIGN_ANGLES)[number];

/** Mirrors `AUDIENCE_SKIP_REASONS`, in the server's order. */
export const AUDIENCE_SKIP_REASONS = [
  "no_company",
  "sole_trader",
  "deleted_company",
  "existing_client",
  "brain_refused",
  "domain_refused",
  "no_domain",
  "no_phone",
  "invalid_phone",
  "foreign_phone",
  "mobile_excluded",
  "suppressed",
  "record_objected",
  "cooldown",
  "line_invalid",
  "already_in_campaign",
  "duplicate_phone",
] as const;
export type AudienceSkipReason = (typeof AUDIENCE_SKIP_REASONS)[number];

/** Mirrors `CAMPAIGN_LIMITS` and `QUEUE_DAYS`. */
export const CAMPAIGN_LIMITS = {
  name: 120,
  dailyCap: { min: 1, max: 500, fallback: 10 },
  maxAttempts: { min: 1, max: 5, fallback: 2 },
  retryAfterHours: { min: 1, max: 168, fallback: 24 },
} as const;
export const QUEUE_DAYS = 2;

/** Mirrors `BrainAudience`. */
export interface BrainAudienceFilter {
  fit?: FitDecision[];
  worked?: BrainWorked;
  followUpDue?: boolean;
  heardFromUs?: boolean;
  reportOpened?: boolean;
  includeClients?: boolean;
  phoneKind?: CampaignPhoneKind;
  company?: AudienceParams;
}

/** Mirrors `RegisterAudience`. */
export type RegisterAudienceFilter = AudienceParams & { includeClients?: boolean; phoneKind?: CampaignPhoneKind };

export type CampaignAudience =
  | { source: "brain"; filter: BrainAudienceFilter }
  | { source: "register"; filter: RegisterAudienceFilter };

/**
 * Mirrors `AiCallCampaignView`. The automation fields (R4) come from
 * `CampaignAutomationView` and stay optional: the draft and the patch read a
 * missing one as the default an older save stands for.
 */
export interface AiCallCampaign extends CampaignAutomationView {
  id: string;
  name: string;
  kind: CampaignKind;
  angle: CampaignAngle;
  status: CampaignStatus;
  source: CampaignSource;
  audience: CampaignAudience | null;
  goal: CampaignGoal;
  pitch: string;
  openingReasons: OpeningReasons;
  openingQuestion: string;
  dailyCap: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  targets: Record<TargetStatus, number>;
}

/** Mirrors `CampaignAudiencePreview`. */
export interface CampaignAudiencePreview {
  unit: "domain" | "company";
  total: number;
  examined: number;
  partial: boolean;
  callable: number;
  skipped: Record<AudienceSkipReason, number>;
  mobiles: number;
  heardFromUs: number;
  sample: Array<{
    key: string;
    companyName: string | null;
    domain: string;
    regCode: string | null;
    phone: string;
    mobile: boolean;
    contact: "letter" | "call" | "none";
  }>;
}

/** Mirrors `EnrolOutcome`. */
export interface EnrolOutcome {
  added: number;
  room: number;
  waiting: number;
  examined: number;
  callable: number;
  skipped: Record<AudienceSkipReason, number>;
  /** Client defect tasks the same press filed (the route adds it; an alert campaign only). */
  clientTasks?: number;
}

/** Mirrors `AiCallTargetView`. */
export interface AiCallTarget {
  id: string;
  regCode: string | null;
  domain: string | null;
  companyName: string | null;
  phone: string;
  legalState: string | null;
  status: string;
  disposition: string | null;
  skipReason: string | null;
  attempts: number;
  lastCallLogId: string | null;
  lastCalledAt: string | null;
  /** R4: when a retry is due, the callback the callee asked for, the last test call, the last call's summary. */
  nextAttemptAt: string | null;
  callbackAt: string | null;
  testCalledAt: string | null;
  summary: string | null;
  createdAt: string;
}

export interface CampaignBody extends Partial<CampaignAutomationBody> {
  name: string;
  kind: CampaignKind;
  angle?: CampaignAngle;
  audience: CampaignAudience;
  goal?: CampaignGoal;
  pitch?: string;
  openingReasons?: Partial<OpeningReasons>;
  openingQuestion?: string;
  dailyCap?: number;
}

export type CampaignPatch = Partial<CampaignBody> & { status?: CampaignStatus };

export const listAiCampaigns = () => api.get<{ rows: AiCallCampaign[] }>(BASE);
export const getAiCampaign = (id: string) => api.get<{ campaign: AiCallCampaign }>(`${BASE}/${encodeURIComponent(id)}`);
export const createAiCampaign = (body: CampaignBody) => api.post<{ campaign: AiCallCampaign }>(BASE, body);
export const patchAiCampaign = (id: string, patch: CampaignPatch) =>
  api.patch<{ campaign: AiCallCampaign }>(`${BASE}/${encodeURIComponent(id)}`, patch);
export const deleteAiCampaign = (id: string) => api.del<{ ok: true }>(`${BASE}/${encodeURIComponent(id)}`);
export const previewAiCampaignAudience = (audience: CampaignAudience, angle: CampaignAngle, campaignId?: string) =>
  api.post<CampaignAudiencePreview>(`${BASE}/preview`, audiencePreviewBody(audience, angle, campaignId));
export const enrolAiCampaign = (id: string) => api.post<EnrolOutcome>(`${BASE}/${encodeURIComponent(id)}/enrol`);
export const listAiCampaignTargets = (id: string, after?: string) =>
  api.get<{ rows: AiCallTarget[]; nextAfter: string | null }>(
    `${BASE}/${encodeURIComponent(id)}/targets?limit=50${after ? `&after=${encodeURIComponent(after)}` : ""}`,
  );

/**
 * The two starting points (plan section 4.5): buttons, not logic of their own.
 *
 * Warm: companies that heard from us and the brain would sell to or wants to
 * wait on. Cold: companies the brain would sell to and nobody has touched yet.
 */
export function campaignPreset(kind: CampaignKind): { kind: CampaignKind; audience: CampaignAudience; goal: CampaignGoal } {
  if (kind === "warm") {
    return { kind, audience: { source: "brain", filter: { heardFromUs: true, fit: ["go", "hold"] } }, goal: "meeting" };
  }
  return { kind, audience: { source: "brain", filter: { fit: ["go"], worked: "untouched" } }, goal: "send_report" };
}

/** The audience preview's body: the angle on screen always, the campaign only when saved. */
export function audiencePreviewBody(
  audience: CampaignAudience,
  angle: CampaignAngle,
  campaignId?: string,
): { audience: CampaignAudience; angle: CampaignAngle; campaignId?: string } {
  return campaignId ? { audience, angle, campaignId } : { audience, angle };
}

/** Every refusal in the preview added up. */
export function skippedTotal(skipped: Partial<Record<AudienceSkipReason, number>>): number {
  return AUDIENCE_SKIP_REASONS.reduce((sum, reason) => sum + (skipped[reason] ?? 0), 0);
}

/** The server's invariant: callable and every refusal are the companies examined. */
export function previewBalances(preview: CampaignAudiencePreview): boolean {
  return preview.callable + skippedTotal(preview.skipped) === preview.examined;
}

/** The refusals worth listing: above zero, largest first, ties in the server's order. */
export function skipRows(skipped: Partial<Record<AudienceSkipReason, number>>): Array<{ reason: AudienceSkipReason; count: number }> {
  return AUDIENCE_SKIP_REASONS.map((reason) => ({ reason, count: skipped[reason] ?? 0 }))
    .filter((row) => row.count > 0)
    .sort((a, b) => b.count - a.count);
}

/** A stable text of an audience, so a debounced preview can tell a real change from a re-render. */
export function audienceKey(audience: CampaignAudience): string {
  const sortKeys = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(sortKeys);
    if (value && typeof value === "object") {
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>)
          .filter(([, v]) => v !== undefined)
          .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
          .map(([k, v]) => [k, sortKeys(v)]),
      );
    }
    return value;
  };
  return JSON.stringify(sortKeys(audience));
}

/** What the editor holds while it is being typed. */
export interface CampaignDraft extends CampaignAutomationDraft {
  name: string;
  kind: CampaignKind;
  angle: CampaignAngle;
  status: CampaignStatus;
  audience: CampaignAudience;
  goal: CampaignGoal;
  pitch: string;
  openingReasons: OpeningReasons;
  openingQuestion: string;
  /** Typed text; checked on the way out. */
  dailyCap: string;
  /** What a switch to an alert overwrote, put back on a switch to an offer (`withAngle`). Never sent. */
  offerBeforeAlert?: OfferFields;
}

export function emptyCampaignDraft(kind: CampaignKind = "warm"): CampaignDraft {
  const preset = campaignPreset(kind);
  return {
    name: "",
    kind,
    angle: "offer",
    status: "draft",
    audience: preset.audience,
    goal: preset.goal,
    pitch: "",
    openingReasons: { letter: "", call: "", cold: "" },
    openingQuestion: "",
    dailyCap: String(CAMPAIGN_LIMITS.dailyCap.fallback),
    ...emptyAutomationDraft(CAMPAIGN_LIMITS),
  };
}

export function toCampaignDraft(c: AiCallCampaign): CampaignDraft {
  return {
    name: c.name,
    kind: c.kind,
    // An older server has no angle: every campaign it holds is an offer.
    angle: c.angle === "alert" ? "alert" : "offer",
    status: c.status,
    // A stored audience the server could not read comes back null; the editor
    // starts the owner on the preset rather than on nothing.
    audience: c.audience ?? campaignPreset(c.kind).audience,
    goal: c.goal,
    pitch: c.pitch,
    openingReasons: { ...c.openingReasons },
    openingQuestion: c.openingQuestion,
    dailyCap: String(c.dailyCap),
    ...toAutomationDraft(c, CAMPAIGN_LIMITS),
  };
}

/** The typed cap as a number inside the bounds, or null when it is not one. */
export function readDailyCap(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return n >= CAMPAIGN_LIMITS.dailyCap.min && n <= CAMPAIGN_LIMITS.dailyCap.max ? n : null;
}

/** The draft as a create body, or null when the name, the cap or an automation field is not valid. */
export function toCampaignBody(d: CampaignDraft): CampaignBody | null {
  const name = d.name.trim();
  const dailyCap = readDailyCap(d.dailyCap);
  const automation = toAutomationBody(d, d.angle, CAMPAIGN_LIMITS);
  if (!name || name.length > CAMPAIGN_LIMITS.name || dailyCap === null || !automation) return null;
  const body: CampaignBody = {
    name,
    kind: d.kind,
    angle: d.angle,
    audience: d.audience,
    goal: d.goal,
    pitch: d.pitch.trim(),
    openingQuestion: d.openingQuestion.trim(),
    dailyCap,
    ...automation,
  };
  // An alert opening never uses the reason slots, and its editor hides them. The
  // server still checks any slot sent, so a failing one would refuse a save the
  // owner cannot see; left out, the stored slots wait for a switch back to offer.
  if (d.angle !== "alert") body.openingReasons = { ...d.openingReasons };
  return body;
}

/**
 * Only what changed against the saved campaign, or null when the draft is not
 * valid. An empty object means nothing to save.
 */
export function toCampaignPatch(d: CampaignDraft, saved: AiCallCampaign): CampaignPatch | null {
  const body = toCampaignBody(d);
  if (!body) return null;
  const patch: CampaignPatch = { ...toAutomationPatch(d, saved, d.angle, CAMPAIGN_LIMITS) };
  if (body.name !== saved.name) patch.name = body.name;
  if (body.kind !== saved.kind) patch.kind = body.kind;
  if (body.angle !== (saved.angle ?? "offer")) patch.angle = body.angle;
  if (d.status !== saved.status) patch.status = d.status;
  if (!saved.audience || audienceKey(body.audience) !== audienceKey(saved.audience)) patch.audience = body.audience;
  if (body.goal !== saved.goal) patch.goal = body.goal;
  if ((body.pitch ?? "") !== saved.pitch) patch.pitch = body.pitch;
  if ((body.openingQuestion ?? "") !== saved.openingQuestion) patch.openingQuestion = body.openingQuestion;
  if (body.dailyCap !== saved.dailyCap) patch.dailyCap = body.dailyCap;
  // Reason slots only for an offer, as in `toCampaignBody`.
  if (d.angle === "alert") return patch;
  const reasons: Partial<OpeningReasons> = {};
  for (const slot of ["letter", "call", "cold"] as const) {
    const next = d.openingReasons[slot].trim();
    if (next !== saved.openingReasons[slot]) reasons[slot] = next;
  }
  if (Object.keys(reasons).length > 0) patch.openingReasons = reasons;
  return patch;
}
